import { useQuery } from '@apollo/client';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { border } from '@topconagriculture/theme';
import { useForwardedRef, useElementScrollbarWidth } from '@topconagriculture/frontend-utils';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { Placeholder } from '../Utils';
const useStyles = makeStyles(() => ({
    container: {
        position: 'relative',
        width: '100%',
        height: '100%',
        flex: 1,
    },
    centerContainer: {
        height: '100%',
    },
    loaderContainer: {
        height: '100%',
        overflow: 'hidden',
    },
    itemLoader: {
        height: 'auto',
        minHeight: 50,
    },
}));
const getItemsCount = (data, initialItemsCount, loading) => {
    if (!data || !data.edges)
        return 0;
    if (data.pageInfo.hasNextPage) {
        if (isEmpty(data.edges))
            return initialItemsCount;
        return data.edges.length + 1;
    }
    return data.edges.length;
};
const isItemLoadedCurried = (data) => (index) => index < data.edges.length;
const extractPaginatedNode = (data, index) => data.edges[index] && data.edges[index].node;
const DEFAULT_ITEM_HEIGHT = 60;
const InfiniteList = (props) => {
    const { gqlQuery, queryOptions, queryParams, dataPath, onError, renderItem, initialItemsCount, itemSize, header, noDataPlaceholder, errorPlaceholder, footer, listOptions, skeletonOptions, customLoaderEl, onGetData, onScrollbarWidthChange, testId = 'InfiniteList', } = props;
    const classes = useStyles();
    const theme = useTheme();
    const thresholds = initialItemsCount || 20;
    const { data, fetchMore, error, loading } = useQuery(gqlQuery, {
        variables: {
            first: thresholds,
            ...queryParams,
        },
        ...queryOptions,
    });
    const extractedData = get(data, dataPath);
    const isItemLoaded = isItemLoadedCurried(extractedData);
    const itemsCount = getItemsCount(extractedData, initialItemsCount, loading);
    const listContainerRef = React.useRef(null);
    const setListContainerRef = useForwardedRef(listContainerRef, listOptions === null || listOptions === void 0 ? void 0 : listOptions.outerRef, (ref) => updateScrollbarWidth(ref));
    const { updateScrollbarWidth, throttledUpdateScrollbarWidth } = useElementScrollbarWidth(listContainerRef.current, onScrollbarWidthChange, [itemsCount]);
    React.useEffect(() => {
        if (data && onGetData) {
            onGetData(data);
        }
    }, [data, onGetData]);
    React.useEffect(() => {
        if (error && typeof onError === 'function') {
            onError(error);
        }
    }, [error, onError]);
    const loadMoreItems = React.useCallback(() => {
        if (!data) {
            return;
        }
        const pageInfo = extractedData.pageInfo;
        if ((pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) && fetchMore) {
            return fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                },
            });
        }
        return;
    }, [data]);
    const handleRenderItem = ({ index, style }) => {
        if (!isItemLoaded(index)) {
            return (React.createElement(Skeleton, Object.assign({ key: `skeleton-${index}`, variant: 'rect', height: (itemSize || DEFAULT_ITEM_HEIGHT) - 1, width: '100%', style: { ...style, borderBottom: border(theme.color.grey.light) } }, skeletonOptions)));
        }
        return renderItem(extractPaginatedNode(extractedData, index), style, index);
    };
    const handleIsItemLoaded = (index) => isItemLoaded(index);
    const listSkeleton = (React.createElement(React.Fragment, null, Array.from(new Array(initialItemsCount)).map((_, index) => (React.createElement(Skeleton, Object.assign({ key: `skeleton-${index}`, variant: 'rect', height: (itemSize || DEFAULT_ITEM_HEIGHT) - 1, width: '100%', style: { borderBottom: border(theme.color.grey.light), minHeight: (itemSize || DEFAULT_ITEM_HEIGHT) - 1 } }, skeletonOptions))))));
    return (React.createElement("div", { className: classes.container, "data-testid": `${testId}-dataPath` }, itemsCount === 0 && loading ? (React.createElement("div", { className: classes.loaderContainer }, customLoaderEl || listSkeleton)) : error ? (React.createElement("div", { className: classes.centerContainer }, errorPlaceholder || (React.createElement(Placeholder, { iconName: "close2", small: true, title: 'Something went wrong trying to retrieve data' })))) : !loading && !itemsCount ? (React.createElement("div", { className: classes.centerContainer }, noDataPlaceholder || React.createElement(Placeholder, { iconName: "warning", small: true, title: 'No data found' }))) : (React.createElement(AutoSizer, { onResize: throttledUpdateScrollbarWidth }, ({ width, height }) => (React.createElement(React.Fragment, null,
        header,
        React.createElement(InfiniteLoader, { threshold: thresholds, minimumBatchSize: thresholds, isItemLoaded: handleIsItemLoaded, itemCount: itemsCount, loadMoreItems: loadMoreItems }, ({ onItemsRendered, ref }) => (React.createElement(FixedSizeList, Object.assign({ onItemsRendered: onItemsRendered, ref: ref, width: width, height: height, itemCount: itemsCount, itemSize: itemSize || (initialItemsCount ? height / initialItemsCount : DEFAULT_ITEM_HEIGHT) }, listOptions, { outerRef: setListContainerRef }), handleRenderItem))),
        footer))))));
};
export default InfiniteList;
//# sourceMappingURL=InfiniteList.js.map